import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, ResponsiveContainer, Legend,
} from 'recharts';
import { getAnalyticsOverview, getAnalyticsTimeseries } from '../api/apiClient';
import './AdminSettings.css';
import './AdminAnalytics.css';

const STATUS_LABEL = { office: 'In Office', home: 'Remote', out: 'Away' };
const STATUS_COLOR = { office: '#22c55e', home: '#3b82f6', out: '#a78bfa' };

const AdminAnalytics = ({ currentUser }) => {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getAnalyticsOverview(currentUser.id),
      getAnalyticsTimeseries(currentUser.id, 14),
    ]).then(([ovRes, tsRes]) => {
      setOverview(ovRes.data);
      setSeries(tsRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [currentUser.id]);

  if (loading) return <div className="ana-wrap"><p className="adm-loading">Loading…</p></div>;
  if (!overview) return <div className="ana-wrap"><p className="adm-loading">Failed to load analytics.</p></div>;

  const presenceData = ['office', 'home', 'out'].map(key => ({
    key,
    name: STATUS_LABEL[key],
    value: overview.presenceToday[key] || 0,
  }));

  const formatDate = (d) => {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="ana-wrap">
      <div className="ana-stats-grid">
        <div className="ana-stat-card">
          <span className="ana-stat-label">Total Staff</span>
          <span className="ana-stat-value">{overview.totalStaff}</span>
        </div>
        <div className="ana-stat-card">
          <span className="ana-stat-label">Active This Week</span>
          <span className="ana-stat-value">{overview.activeUsersThisWeek}</span>
        </div>
        <div className="ana-stat-card">
          <span className="ana-stat-label">Logins Today</span>
          <span className="ana-stat-value">{overview.loginsToday}</span>
        </div>
        <div className="ana-stat-card">
          <span className="ana-stat-label">New This Month</span>
          <span className="ana-stat-value">{overview.newStaffThisMonth}</span>
        </div>
      </div>

      <div className="ana-charts-row">
        <div className="ana-panel">
          <h3 className="ana-panel-title">Activity — Last 14 Days</h3>
          <div className="ana-chart-box">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 5, right: 12, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 11, fill: '#999' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#999' }} />
                <Tooltip labelFormatter={formatDate} />
                <Area type="monotone" dataKey="activity" name="Total activity" stroke="#272e40" fill="#272e40" fillOpacity={0.08} strokeWidth={2} />
                <Area type="monotone" dataKey="logins" name="Logins" stroke="#1a73e8" fill="#1a73e8" fillOpacity={0.12} strokeWidth={2} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="ana-panel">
          <h3 className="ana-panel-title">Today's Presence</h3>
          <div className="ana-chart-box">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={presenceData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {presenceData.map(entry => (
                    <Cell key={entry.key} fill={STATUS_COLOR[entry.key]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="ana-legend">
            {presenceData.map(d => (
              <div key={d.key} className="ana-legend-row">
                <span className="ana-legend-dot" style={{ background: STATUS_COLOR[d.key] }} />
                <span>{d.name}</span>
                <span className="ana-legend-count">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="ana-panel">
        <h3 className="ana-panel-title">Most Active This Week</h3>
        {overview.topActive.length === 0 ? (
          <p className="adm-loading">No activity recorded yet.</p>
        ) : (
          <ul className="ana-leaderboard">
            {overview.topActive.map((person, i) => {
              const initials = person.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
              return (
                <li key={person.id} className="ana-leader-row">
                  <span className="ana-leader-rank">{i + 1}</span>
                  <div className="adm-avatar">
                    {person.avatar
                      ? <img src={person.avatar} alt={person.name} />
                      : <span>{initials}</span>}
                  </div>
                  <span className="ana-leader-name">{person.name}</span>
                  <span className="ana-leader-count">{person.count}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
